import { createSelector } from "@reduxjs/toolkit";

import { ENVIRONMENT_TYPES } from "Constants/constants";
import { UserWithAccesses } from "Reducers/access/environmentTypeAccess/environmentTypeAccess.types";
import { selectProjectAccesses } from "Reducers/access/projectAccess/projectAccess.selectors";

import type { RootState } from "Store/configureStore";

export const selectEnvironmentTypeAccesses = (state: RootState) =>
  state.environmentType;

export const selectEnvironmentTypeAccessesAsJS = createSelector(
  selectEnvironmentTypeAccesses,
  accesses => accesses.data
);

// Transforms from the object we get from the API (An object with
// environment type as key and users listed under each environment type)
// to what we need in the UI, an object with user ids as keys and permissions
// for each environment type listed after them
export const selectAccessesForView = createSelector(
  selectProjectAccesses,
  selectEnvironmentTypeAccessesAsJS,
  (projectAccesses, environmentTypeAccesses) => {
    const result: Record<string, UserWithAccesses> = {};

    const partialAccesses = projectAccesses.reduce<
      Record<string, Pick<UserWithAccesses, "role" | "projectAccess">>
    >((acc, projectAccess) => {
      const userId = projectAccess?.getUser().id;
      acc[userId] = {
        role: projectAccess.role,
        projectAccess: projectAccess
      };
      return acc;
    }, {});

    environmentTypeAccesses.forEach(environmentType => {
      environmentType.accesses.forEach(access => {
        // If the user is still not in the object, add it
        const userData = access.getUser();

        if (!userData || !access.user || !partialAccesses[access.user]) {
          return;
        }

        result[access.user] = {
          ...partialAccesses[access.user],
          id: access.user,
          name: userData.display_name,
          email: userData.email,
          environments: {
            ...(result[access.user]?.environments || {}),
            [environmentType.id]: {
              access,
              role: access.role
            }
          }
        };
      });
    });

    // Now we need to fill in the missing environment types with no-access
    // since the API does not return a value when no environment type is present
    ENVIRONMENT_TYPES.forEach(environmentType => {
      Object.values(result).forEach(user => {
        if (user.environments && !user.environments[environmentType]) {
          user.environments[environmentType] = {
            role: "no-access",
            ...(user.environments[environmentType] || {})
          };
        }
      });
    });

    return result;
  }
);

const selectUserId = (_: RootState, props: { userId: string }) => {
  return props.userId;
};

export const selectAccessesForSingleUser = createSelector(
  selectAccessesForView,
  selectUserId,
  (accesses, userId) => accesses[userId]
);

export const selectAccessesAreLoading = (state: RootState) =>
  state.environmentType.loading;

export const selectUserRole = createSelector(
  selectAccessesForSingleUser,
  accesses => {
    return accesses?.role;
  }
);
